import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { LOGO_URL } from '../data/business';
import { DOORDASH_URL, UBEREATS_URL, PHONE_NUMBER, PHONE_DISPLAY } from '../data/menu';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const orderRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, []);

  // Close order dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orderRef.current && !orderRef.current.contains(e.target)) {
        setIsOrderOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.3 }
    })
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'backdrop-blur-xl bg-white/90 shadow-md border-b border-gray-200' 
          : 'bg-white/80'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo with Animation */}
          <motion.a 
            href="#hero"
            onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="header-logo"
          >
            <motion.img 
            src={LOGO_URL} 
              alt="Salam Grill" 
              className="h-28 w-auto"
              initial={{ rotate: -10 }}
              animate={{ rotate: 0 }}
              transition={{ type: "spring" }}
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { id: 'menu', label: 'menu', target: 'menu' },
              { id: 'about', label: 'about', target: 'experience' },
              { id: 'contact', label: 'contact', target: 'location' }
            ].map((item, i) => (
              <motion.button
                key={item.id}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={navItemVariants}
                onClick={() => scrollToSection(item.target)}
                className="relative text-[#6B1C23] hover:text-[#8B2430] transition-colors font-manrope font-medium group"
                data-testid={`nav-${item.id}`}
                whileHover={{ y: -2 }}
              >
                {t(`nav.${item.label}`)}
                <motion.span 
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#6B1C23] group-hover:w-full transition-all duration-300"
                  whileHover={{ width: "100%" }}
                />
              </motion.button>
            ))}

            {/* Catering — Special Highlighted */}
            <motion.button
              initial="hidden"
              animate="visible"
              variants={navItemVariants}
              custom={3}
              onClick={() => scrollToSection('catering')}
              className="px-4 py-1.5 bg-[#6B1C23] text-white rounded-full font-manrope font-semibold text-sm hover:bg-[#8B2430] transition-colors shadow-sm"
              data-testid="nav-catering"
              whileHover={{ scale: 1.05, boxShadow: '0 4px 15px rgba(107,28,35,0.3)' }}
              whileTap={{ scale: 0.95 }}
            >
              {t('nav.catering')}
            </motion.button>
          </nav>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Call Button */}
            <motion.a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center gap-2 text-[#6B1C23] hover:text-[#8B2430] transition-colors font-manrope"
              data-testid="header-call-btn"
              whileHover={{ scale: 1.05 }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
              >
                <Phone className="w-4 h-4" />
              </motion.div>
              <span className="hidden lg:inline">{PHONE_DISPLAY}</span>
            </motion.a>

            {/* Order Now Dropdown */}
            <div className="relative" ref={orderRef}>
              <motion.button
                onClick={() => setIsOrderOpen(!isOrderOpen)}
                className="flex items-center gap-1.5 bg-[#6B1C23] text-white hover:bg-[#8B2430] transition-all font-manrope font-medium px-5 py-2 rounded-md"
                data-testid="header-order-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('nav.order')}
                <motion.div animate={{ rotate: isOrderOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {isOrderOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-[#e8e0d4] overflow-hidden z-50"
                    data-testid="order-dropdown"
                  >
                    <a
                      href={DOORDASH_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#faf7f2] transition-colors font-manrope text-sm text-[#333] font-medium"
                      data-testid="order-doordash"
                      onClick={() => setIsOrderOpen(false)}
                    >
                      <span className="w-2 h-2 rounded-full bg-[#FF3008]" />
                      DoorDash
                    </a>
                    <a
                      href={UBEREATS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#faf7f2] transition-colors font-manrope text-sm text-[#333] font-medium border-t border-[#f0ebe3]"
                      data-testid="order-ubereats"
                      onClick={() => setIsOrderOpen(false)}
                    >
                      <span className="w-2 h-2 rounded-full bg-[#142328]" />
                      Uber Eats
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#6B1C23] p-2"
            data-testid="mobile-menu-toggle"
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="w-6 h-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu with Animation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'calc(100vh - 88px)' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-200 overflow-hidden overflow-y-auto"
          >
            <motion.div 
              className="px-4 py-6 pb-12 space-y-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {['menu', 'about', 'contact'].map((item, i) => (
                <motion.button
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => scrollToSection(item === 'about' ? 'experience' : item === 'contact' ? 'location' : item)}
                  className="block w-full text-left text-[#6B1C23] hover:text-[#8B2430] py-2 font-manrope"
                  data-testid={`mobile-nav-${item}`}
                >
                  {t(`nav.${item}`)}
                </motion.button>
              ))}

              {/* Catering — Special in mobile too */}
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => scrollToSection('catering')}
                className="block w-full text-left bg-[#6B1C23] text-white rounded-md px-4 py-2.5 font-manrope font-semibold"
                data-testid="mobile-nav-catering"
              >
                {t('nav.catering')}
              </motion.button>
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="pt-4 border-t border-gray-200 space-y-3"
              >
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  className="flex items-center gap-2 text-[#6B1C23] py-2 font-manrope"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  {PHONE_DISPLAY}
                </a>
                
                <Button
                  asChild
                  className="w-full bg-[#6B1C23] text-white hover:bg-[#8B2430]"
                  data-testid="mobile-order-doordash-btn"
                >
                  <a href={DOORDASH_URL} target="_blank" rel="noopener noreferrer">
                    DoorDash
                  </a>
                </Button>
                <Button
                  asChild
                  className="w-full bg-[#142328] text-white hover:bg-[#1f3640]"
                  data-testid="mobile-order-ubereats-btn"
                >
                  <a href={UBEREATS_URL} target="_blank" rel="noopener noreferrer">
                    Uber Eats
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-[#FBBC05] text-[#2C2C2C] hover:bg-[#FBBC05]/10"
                  data-testid="mobile-review-btn"
                >
                  <a href="https://g.page/r/CbKi5__xhDr7EAE/review" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                    Leave a Review
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
